// LIVE DATA — the lead page's Payments panel: what a lead has paid us, read
// from Stripe on every visit, beside the billing state HubSpot holds.
//
// The path: GET /api/payments?emails=&domains= → the sales stack's
// customer-payments lambda (terraform: modules/lambda/customer_payments.tf),
// which checks the caller's sign-in and workspace membership → production's
// read-only customer-payments-lookup function (blueprints-terraform:
// customer-payments-lookup.tf), which holds the Stripe key and only ever sends
// GET. A Stripe customer belongs to the lead when its email is one of the
// lead's emails or sits on the lead's company domain; free-mail domains never
// match by domain (production enforces it; this file just doesn't ask).
//
// What comes back, snake_case and in Stripe's units (amounts in the smallest
// currency unit, times in unix seconds):
//   customers      [{ id, email, name, created, delinquent, matched_by, dashboard_url }]
//   payments       [{ id, customer_email, amount, amount_refunded, currency, status,
//                     paid, created, description, failure_message, card, dashboard_url }]
//   subscriptions  [{ id, customer_email, status, product, amount, currency, interval,
//                     interval_count, started, current_period_end, trial_end,
//                     cancel_at_period_end, canceled_at, ended_at, dashboard_url }]
//   open_invoices  [{ id, number, customer_email, status (open | uncollectible),
//                     amount_due, amount_remaining, currency, created, due_date,
//                     attempt_count, next_payment_attempt, dashboard_url }]
//   totals         { <currency>: { paid, refunded, payments, failed, outstanding } }
//
// HubSpot's half rides on the mirrored contacts the page already has: each
// contact's `billing` (trial end, subscription end, card on file, payment-link
// status) — see hubspotBilling below.
//
// Everything below the fetch is pure, pinned by scripts/test-payments.mjs.
import { getIdToken } from './cognito.js'

// Same escape hatch every other API module here has: `npm run start` without
// API_PROXY answers /api/* with index.html and a 200, so a non-JSON response
// means "no API on this origin" — retry the deployed stage directly.
const PAYMENTS_FALLBACK_BASE = 'https://hzpio22kcl.execute-api.us-east-1.amazonaws.com/prod'

export const MAX_EMAILS = 25
export const MAX_DOMAINS = 3

// Free-mail domains say nothing about which company someone works for. Not
// sent as domains at all (production refuses them too).
export const FREE_MAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'yahoo.com', 'ymail.com', 'outlook.com', 'hotmail.com',
  'live.com', 'msn.com', 'icloud.com', 'me.com', 'mac.com', 'aol.com', 'proton.me',
  'protonmail.com', 'gmx.com', 'mail.com', 'comcast.net', 'att.net', 'sbcglobal.net',
  'verizon.net', 'zoho.com',
])

const EMAIL_RE = /^[^@\s'"\\]{1,64}@[a-z0-9.-]{1,253}\.[a-z]{2,}$/

function domainOf(website) {
  const host = String(website || '')
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, '')
    .split(/[/?#]/)[0]
    .replace(/:\d+$/, '')
    .replace(/^www\./, '')
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(host) ? host : ''
}

/**
 * What to ask Stripe about one lead: every email the page knows for it, and
 * its company domains — the website's, plus the business domain its people
 * actually write from when that differs (a lead at acme.com whose staff mail
 * from acmebuilds.com). Deterministic, so it doubles as a cache/effect key.
 */
export function paymentLookup({ website, emails } = {}) {
  const cleanEmails = []
  for (const raw of emails || []) {
    const e = String(raw || '').trim().toLowerCase()
    if (EMAIL_RE.test(e) && !cleanEmails.includes(e)) cleanEmails.push(e)
  }
  cleanEmails.sort()
  const domains = []
  const site = domainOf(website)
  if (site && !FREE_MAIL_DOMAINS.has(site)) domains.push(site)
  const counts = new Map()
  for (const e of cleanEmails) {
    const d = e.split('@')[1]
    if (!FREE_MAIL_DOMAINS.has(d) && !domains.includes(d)) counts.set(d, (counts.get(d) || 0) + 1)
  }
  for (const [d] of [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))) {
    if (domains.length >= MAX_DOMAINS) break
    domains.push(d)
  }
  const out = { emails: cleanEmails.slice(0, MAX_EMAILS), domains }
  out.key = `${out.emails.join(',')}|${out.domains.join(',')}`
  return out
}

/**
 * The lead's Stripe payments. Throws an Error carrying `status` (0 when the
 * API could not be reached) and the server's own words, so the panel can tell
 * an expired session (401) from a non-member (403) from Stripe being
 * unreadable (502).
 */
export async function fetchLeadPayments({ emails = [], domains = [] } = {}) {
  const token = await getIdToken()
  const init = { headers: token ? { Authorization: `Bearer ${token}` } : {} }
  const query = `payments?emails=${encodeURIComponent(emails.join(','))}&domains=${encodeURIComponent(domains.join(','))}`
  const get = async (url) => {
    try {
      return await fetch(url, init)
    } catch {
      return null
    }
  }
  const isJson = (r) => (r ? (r.headers.get('content-type') || '').includes('application/json') : false)
  let res = await get(`/api/${query}`)
  if (!isJson(res)) res = await get(`${PAYMENTS_FALLBACK_BASE}/${query}`)
  if (!res) {
    const err = new Error('Payments could not be reached from this browser.')
    err.status = 0
    throw err
  }
  if (!isJson(res)) {
    const err = new Error(`Payments are unavailable (${res.status}).`)
    err.status = res.status
    throw err
  }
  const data = await res.json()
  if (!res.ok) {
    const err = new Error(data.error || `Payments failed (${res.status}).`)
    err.status = res.status
    throw err
  }
  return data
}

// ---- formatting ----------------------------------------------------------------

// Stripe's zero-decimal currencies: their amounts are already whole units.
const ZERO_DECIMAL = new Set(['bif', 'clp', 'djf', 'gnf', 'jpy', 'kmf', 'krw', 'mga', 'pyg', 'rwf', 'ugx', 'vnd', 'vuv', 'xaf', 'xof', 'xpf'])

/** 14970, 'usd' → '$149.70' */
export function formatMoney(amount, currency = 'usd') {
  if (amount == null || !Number.isFinite(Number(amount))) return '—'
  const code = String(currency || 'usd').toLowerCase()
  const value = ZERO_DECIMAL.has(code) ? Number(amount) : Number(amount) / 100
  try {
    return value.toLocaleString(undefined, { style: 'currency', currency: code.toUpperCase() })
  } catch {
    return `${value.toFixed(2)} ${code.toUpperCase()}`
  }
}

/** unix seconds → Date, or null. */
export function fromUnix(seconds) {
  const n = Number(seconds)
  return Number.isFinite(n) && n > 0 ? new Date(n * 1000) : null
}

export const PAYMENT_STATUS = {
  succeeded: { label: 'Paid', tone: 'success' },
  refunded: { label: 'Refunded', tone: 'neutral' },
  partially_refunded: { label: 'Partly refunded', tone: 'warning' },
  failed: { label: 'Failed', tone: 'danger' },
  pending: { label: 'Pending', tone: 'info' },
}

// Invoices still owed: open (Stripe is still collecting) or uncollectible
// (Stripe gave up). Paid invoices are already in the payments.
export const INVOICE_STATUS = {
  open: { label: 'Unpaid', tone: 'warning' },
  uncollectible: { label: 'Uncollectible', tone: 'danger' },
}

export const SUBSCRIPTION_STATUS = {
  active: { label: 'Active', tone: 'success' },
  trialing: { label: 'Trial', tone: 'info' },
  past_due: { label: 'Past due', tone: 'danger' },
  unpaid: { label: 'Unpaid', tone: 'danger' },
  incomplete: { label: 'Incomplete', tone: 'warning' },
  incomplete_expired: { label: 'Expired', tone: 'neutral' },
  paused: { label: 'Paused', tone: 'warning' },
  canceled: { label: 'Canceled', tone: 'neutral' },
}

const INTERVAL_WORD = { day: 'day', week: 'week', month: 'month', year: 'year' }

/** '$149.70 / month', '$1,500.00 / 3 months', '' when there is no price. */
export function subscriptionPrice(s) {
  if (s?.amount == null) return ''
  const word = INTERVAL_WORD[s.interval] || s.interval || ''
  if (!word) return formatMoney(s.amount, s.currency)
  const count = Number(s.interval_count || 1)
  return `${formatMoney(s.amount, s.currency)} / ${count > 1 ? `${count} ${word}s` : word}`
}

/**
 * The one date that matters for a subscription right now, said in words:
 * when it renews, when its trial ends, when it ends, or when it ended.
 */
export function subscriptionWhen(s) {
  const day = (seconds) =>
    fromUnix(seconds)?.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) || ''
  if (s.status === 'canceled' || s.ended_at) return s.ended_at || s.canceled_at ? `ended ${day(s.ended_at || s.canceled_at)}` : 'ended'
  if (s.status === 'trialing' && s.trial_end) return `trial ends ${day(s.trial_end)}`
  if (s.cancel_at_period_end && s.current_period_end) return `ends ${day(s.current_period_end)}`
  if (s.current_period_end) return `renews ${day(s.current_period_end)}`
  return ''
}

/**
 * The panel's headline numbers, per currency (a lead almost always pays in
 * one; two currencies are shown side by side rather than added up).
 */
export function summarizePayments(raw) {
  const totals = raw?.totals || {}
  const currencies = Object.keys(totals).sort((a, b) => (totals[b].paid || 0) - (totals[a].paid || 0))
  const payments = raw?.payments || []
  const subscriptions = raw?.subscriptions || []
  const live = subscriptions.filter((s) => ['active', 'trialing', 'past_due', 'unpaid'].includes(s.status))
  return {
    paid: currencies.map((c) => formatMoney(totals[c].paid, c)).join(' + ') || formatMoney(0),
    refunded: currencies.filter((c) => totals[c].refunded).map((c) => formatMoney(totals[c].refunded, c)).join(' + '),
    // Money owed on open and uncollectible invoices.
    outstanding: currencies.filter((c) => totals[c].outstanding).map((c) => formatMoney(totals[c].outstanding, c)).join(' + '),
    openInvoiceCount: (raw?.open_invoices || []).length,
    paymentCount: currencies.reduce((n, c) => n + (totals[c].payments || 0), 0),
    failedCount: currencies.reduce((n, c) => n + (totals[c].failed || 0), 0),
    lastPaymentAt: fromUnix(raw?.last_payment_at),
    firstPaymentAt: fromUnix(payments.filter((p) => p.paid).reduce((min, p) => (min && min < p.created ? min : p.created), 0)),
    // When the oldest matched Stripe customer was created — a customer on a
    // fully discounted plan has never paid, but has been a customer since then.
    customerSince: fromUnix((raw?.customers || []).reduce((min, c) => (min && min < c.created ? min : c.created), 0)),
    liveSubscriptions: live.length,
    pastDue: live.some((s) => s.status === 'past_due' || s.status === 'unpaid'),
  }
}

/**
 * HubSpot's billing state for the lead's mirrored contacts — only the people
 * HubSpot actually holds something for. Empty fields stay empty: a contact
 * who is not a portal user has no card on file to be missing.
 */
export function hubspotBilling(contacts) {
  const rows = []
  for (const c of contacts || []) {
    const b = c?.billing || {}
    const has =
      b.trial_ends_at || b.subscription_ends_at || b.payment_link_status || b.payment_link_created_at ||
      b.last_payment_failed_at || b.payment_method_attached != null
    if (!has) continue
    rows.push({
      key: c.id || c.email || c.name,
      name: c.name || c.email || 'Unnamed contact',
      email: c.email || '',
      hubspotUrl: c.hubspot_url || '',
      trialEndsAt: b.trial_ends_at || '',
      subscriptionEndsAt: b.subscription_ends_at || '',
      paymentMethodAttached: b.payment_method_attached ?? null,
      paymentLinkStatus: b.payment_link_status || '',
      paymentLinkCreatedAt: b.payment_link_created_at || '',
      lastPaymentFailedAt: b.last_payment_failed_at || '',
    })
  }
  return rows
}
